import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Global top-3 badge map, fetched once per app session and cached in
 * module scope. Any component (VideoCard, Profile, Search) can call
 * `useTopBadges(userId)` to get `{category, rank}` if that user is
 * top-3 in either the sharer or referrer leaderboard, or `null`
 * otherwise. Backend caches the aggregate for 120s so this is cheap.
 */
let _cache = null;
let _pending = null;

async function loadBadges() {
  if (_cache) return _cache;
  if (_pending) return _pending;
  _pending = api
    .get("/badges/top")
    .then((r) => {
      _cache = r.data || { sharers: {}, referrers: {} };
      return _cache;
    })
    .catch(() => {
      _cache = { sharers: {}, referrers: {} };
      return _cache;
    })
    .finally(() => { _pending = null; });
  return _pending;
}

// Notify listeners after the initial fetch resolves so hooks that mounted
// before it completed can re-render with the real data.
const subscribers = new Set();

export function useTopBadges(userId) {
  const [data, setData] = useState(_cache);

  useEffect(() => {
    if (_cache) {
      setData(_cache);
      return;
    }
    const notify = (d) => setData(d);
    subscribers.add(notify);
    loadBadges().then((d) => {
      subscribers.forEach((fn) => fn(d));
    });
    return () => subscribers.delete(notify);
  }, []);

  if (!data || !userId) return null;
  // Referrer badge outranks sharer when a user has both (referral value
  // is worth more than shares — literal cash commissions).
  if (data.referrers?.[userId]) {
    return { category: "referrer", rank: data.referrers[userId] };
  }
  if (data.sharers?.[userId]) {
    return { category: "sharer", rank: data.sharers[userId] };
  }
  return null;
}

/** Force a re-fetch — call after a big share/signup event if you want. */
export function invalidateBadges() {
  _cache = null;
}
